<script lang="ts" setup>
    import { onMounted, reactive, computed } from 'vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import TextInput from '../../components/TextInput.vue'
    import { getObject, postObject } from '../../lib/ChoiceApi';
    import { isValidPhone, isValidEmail } from '../../lib/Validation';
    import { MessageSectionType, DisplayStyle } from "../../Enums/UiEnums";
    import * as choiceApi from '../../lib/ChoiceApi';
    import { ActionButtonStyle} from '../../lib/Types/Types';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import { ButtonDisplayStyle } from "../../Enums/UiEnums";
    import '../../../css/choice.css';
    import MessageSection from '../../components/Messages/MessageSection.vue';
    import { SMSorEmailCommunicationType } from '../../Enums/FormEnums';

    interface ICommunicationOption {
        type: SMSorEmailCommunicationType,
        value: string
    }

    const communicationOptions: ICommunicationOption[] = [
        { type: SMSorEmailCommunicationType.TextHome, value: "Text Message Home" },
        { type: SMSorEmailCommunicationType.TextWork, value: "Text Message Work" },
        { type: SMSorEmailCommunicationType.TextOther, value: "Text Message Other" },
        { type: SMSorEmailCommunicationType.Email, value: "Email" },
        { type: SMSorEmailCommunicationType.TextOverride, value: "Text Override" },
        { type: SMSorEmailCommunicationType.EmailOverride, value: "Email Override" },
    ];

    let formData = reactive<{
        communicationType?: SMSorEmailCommunicationType,
        communicationValue?: string,
        override?: string,
        patientEntityId: number,
        receiptAmount: string,
        success?: string,
        error?: string
    }>({
        communicationType: undefined,
        communicationValue: undefined,
        override: undefined,
        patientEntityId: 0,
        receiptAmount: '0',
        success: undefined,
        error: undefined
    });

    const extractQueryParams = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const patientId = Number(urlParams.get('pat_id'));
        let patientEntityId = Number(urlParams.get('pat_eid'));
        if (!patientEntityId) {
            const patient_entity = await choiceApi.patient.entity(patientId);
            patientEntityId = patient_entity?.entity_id ?? 0;
        }
        const receiptAmount = urlParams.get('amt');
        formData.patientEntityId = Number(patientEntityId);
        formData.receiptAmount = parseFloat(receiptAmount ?? '0').toFixed(2);
    };

    onMounted(async () => {
        // look at ttp setup component
        await extractQueryParams();
        const response = await choiceApi.patient.getPatientReceiptPreference(formData.patientEntityId);
        if (response) {
            formData.communicationType = response.type;
            formData.communicationValue = communicationOptions.find(option => option.type == response.type)?.value ?? "";
            formData.override = response.overrideValue;
        }
    });

    const handleSelectionChange = (value: any) => {
        const index = value.target.selectedIndex;
        formData.communicationType = communicationOptions[index].type;
        formData.communicationValue = communicationOptions[index].value;
        formData.override = undefined;
        formData.error = undefined;
    };

    const overrideDisabled = computed(() => {
        return formData.communicationType != SMSorEmailCommunicationType.TextOverride
                && formData.communicationType != SMSorEmailCommunicationType.EmailOverride;
    });

    async function SendReceipt() {
        if (!overrideDisabled && !formData.override) {
            formData.error = "An override value was not provided";
            return;
        }
        if (formData.communicationType == SMSorEmailCommunicationType.TextOverride && !isValidPhone(formData.override)) {
            formData.error = "Phone override is not a valid phone number";
            return;
        }
        if (formData.communicationType == SMSorEmailCommunicationType.EmailOverride && !isValidEmail(formData.override)) {
            formData.error = "Email override is not a valid email";
            return;
        }

        const payload = {
            entityId: formData.patientEntityId,
            type: formData.communicationType,
            overrideValue: formData.override,
            receiptAmount: formData.receiptAmount
        };
        const response = await choiceApi.billing.generateEReceipt(payload);
        if (response) {
            formData.error = undefined;
            formData.success = "Successfully sent E-Receipt";
            setTimeout(close, 2000);
        } else {
            formData.error = "An error occurred while attempting to send E-Receipt";
        }
    }

    async function close() {
        window.close();
    }

</script>

<style>
    .EReceiptLabel {
        display: flex;
        justify-content: end;
        width: 10rem;
    }
    .Input {
        width: 20rem;
    }
    select, option {
        padding: 4px;
        font-size: 0.9rem;
    }
    #messageSection {
        margin-left: 1.5rem;
    }
</style>

<template>
    <div class="row" style="margin-top:4rem;">
        <div class="EReceiptLabel mt-1 label-isalus-small" style="font-weight: bold;">
            E-Receipt Selection
        </div>
        <div class="Input">
            <select v-model="formData.communicationValue" @change="handleSelectionChange">
                <option v-for="option in communicationOptions" :key="option.type" :value="option.value">
                    {{ option.value }}
                </option>
            </select>
        </div>
    </div>
    <div class="row mt-2">
        <div class="EReceiptLabel mt-1 label-isalus-small" style="font-weight: bold;">
            Override
        </div>
        <div class="Input">
            <TextInput 
                v-model:text-value="formData.override" 
                :maxLength="50"
                :cssFramework = "'vanilla'"
                :disabled="overrideDisabled">
            </TextInput>
        </div>
    </div>
    <div class="row mt-5">
        <div class="col-auto" style="display: flex; justify-content: end; width: 500px; gap: 4px;">
            <ActionButtonV2 
                :action="ActionButtonStyle.Save"
                :base-text="'Send'"
                :text="'Send'"
                :size="ButtonDisplaySize.Small" 
                :spinText="'Sending'"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="SendReceipt"
            />
            <ActionButtonV2 
                :action="ActionButtonStyle.Close"
                :base-text="'Cancel'"
                :text="'Cancel'"
                :size="ButtonDisplaySize.Small" 
                :spinText="'Cancel'"
                :displayStyle="ButtonDisplayStyle.OutlineSecondary"  
                :performAction="close"
            />
        </div>
    </div>
    <div class="row mt-2">
        <div id="messageSection" class="col mt-2 label-isalus-small">
            <MessageSection v-if="formData.error" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{formData.error}}
            </MessageSection>
            <MessageSection v-if="formData.success" :type="MessageSectionType.Text" :display-style="DisplayStyle.Success">
                {{formData.success}}
            </MessageSection>
        </div>
    </div> 
</template>

